import {inject, Injectable, NgZone} from '@angular/core';
import {Router} from '@angular/router';

import {AppLogger, AppLoggerService} from '../../core/logging';

/** What a local notification needs to show and where its click leads. */
export interface LocalNotification {
  title: string;
  body: string;
  /** In-app route the click navigates to. */
  route: string | unknown[];
  /**
   * Collapse key. A second notification with the same tag replaces the first
   * rather than stacking, which is what "one notification per round" means on
   * the browser side.
   */
  tag?: string;
}

export type LocalNotificationOutcome = 'shown' | 'denied' | 'unsupported';

/**
 * Shows a real OS notification from the browser and routes the app when it is
 * clicked.
 *
 * This is not push: nothing arrives while the tab is closed. It exists because
 * the web app has no push channel at all, and the click-through path — see the
 * notification, click it, land on the right screen — still has to be buildable
 * and testable. A server-sent push on a phone and this share the same
 * destination, so the routing is exercised either way.
 */
@Injectable({providedIn: 'root'})
export class LocalNotificationService {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly log: AppLogger = inject(AppLoggerService).createForClass(LocalNotificationService);

  /** Whether this browser can show notifications at all. */
  get supported(): boolean {
    return typeof Notification !== 'undefined';
  }

  /** Current permission, or 'unsupported' where the API is missing. */
  get permission(): NotificationPermission | 'unsupported' {
    return this.supported ? Notification.permission : 'unsupported';
  }

  /**
   * Shows a notification, asking for permission first if it has never been
   * decided. Returns what actually happened rather than throwing — a blocked
   * notification is a normal outcome the caller has to tell the user about.
   */
  async show(notification: LocalNotification): Promise<LocalNotificationOutcome> {
    if (!this.supported) return 'unsupported';

    let permission = Notification.permission;
    if (permission === 'default') {
      // Must be called from a user gesture; the caller's click is that gesture.
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') return 'denied';

    const shown = new Notification(notification.title, {
      body: notification.body,
      tag: notification.tag,
      icon: 'favicon.ico',
    });

    shown.onclick = () => {
      // The notification handler runs outside Angular; navigation has to be
      // brought back in or the view never updates.
      this.zone.run(() => {
        window.focus();
        const target = Array.isArray(notification.route) ? notification.route : [notification.route];
        void this.router.navigate(target);
      });
      shown.close();
    };

    shown.onerror = () => this.log.warn('local notification failed to display');

    return 'shown';
  }
}
